import React, { useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";
import { ArrowUpRight, TrendingUp, TrendingDown, ExternalLink, RefreshCw, CandlestickChart, LineChart } from "lucide-react";
import InteractiveChart from "./InteractiveChart";
import Range52WBar from "./Range52WBar";
import { COMPANY } from "../data/content";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Canonical external URLs — verified against live services.
const TRADINGVIEW_SYMBOL_URL = "https://www.tradingview.com/symbols/OTC-GCAN/";
const OTC_MARKETS_URL = "https://www.otcmarkets.com/stock/GCAN/quote";
const SEC_EDGAR_CIK_URL =
  "https://www.sec.gov/cgi-bin/browse-edgar?action=getcompany&CIK=0001695473&type=&dateb=&owner=include&count=40";

const RANGE_PILLS = ["1M", "3M", "6M", "1Y", "5Y"];
const DEFAULT_RANGE = "3M";

function fmtPrice(n) {
  if (n === null || n === undefined || Number.isNaN(Number(n))) return "—";
  const v = Number(n);
  if (v >= 100) return v.toFixed(2);
  return v.toFixed(4);
}
function fmtChange(n) {
  if (n === null || n === undefined || Number.isNaN(Number(n))) return "—";
  const v = Number(n);
  return `${v > 0 ? "+" : ""}${v.toFixed(4)}`;
}
function fmtPct(n) {
  if (n === null || n === undefined || Number.isNaN(Number(n))) return "—";
  const v = Number(n);
  return `${v > 0 ? "+" : ""}${v.toFixed(2)}%`;
}
function fmtVolume(n) {
  if (n === null || n === undefined || Number.isNaN(Number(n))) return "—";
  const v = Number(n);
  if (v >= 1e9) return (v / 1e9).toFixed(2) + "B";
  if (v >= 1e6) return (v / 1e6).toFixed(2) + "M";
  if (v >= 1e3) return (v / 1e3).toFixed(1) + "K";
  return String(v);
}
function fmtQuoteTime(date, time) {
  if (!date && !time) return "—";
  return [date, time].filter(Boolean).join(" ");
}

/**
 * LiveQuotePanel — Bloomberg-terminal style quote panel:
 * candlestick / line toggle, volume histogram, live OHLCV readout strip,
 * range pills, 52-week range bar. Zero third-party runtime scripts.
 */
export default function LiveQuotePanel({ renderFallback }) {
  const [snap, setSnap] = useState(null);
  const [range, setRange] = useState(DEFAULT_RANGE);
  const [chartType, setChartType] = useState("candles");
  const [loading, setLoading] = useState(true);
  const [rangeLoading, setRangeLoading] = useState(false);
  const [loadErr, setLoadErr] = useState(null);
  const [readout, setReadout] = useState(null);
  const [pinned, setPinned] = useState(null); // {date, open, high, low, close, volume} or null

  const load = useCallback(async (r, isInitial = false) => {
    try {
      if (isInitial) setLoading(true); else setRangeLoading(true);
      const resp = await axios.get(`${API}/quote/gcan`, {
        params: { range: r }, timeout: 15000,
      });
      setSnap(resp.data || null);
      setLoadErr(null);
    } catch (e) {
      setLoadErr("Unable to reach the quote service. Please try again shortly.");
    } finally {
      if (isInitial) setLoading(false); else setRangeLoading(false);
    }
  }, []);

  useEffect(() => { load(DEFAULT_RANGE, true); }, [load]);

  const onSelectRange = useCallback((r) => {
    if (r === range) return;
    setRange(r);
    setReadout(null);
    load(r, false);
  }, [range, load]);

  // Pin: click/tap a bar → toggle pin. If clicking the SAME bar, unpin.
  const onBarClick = useCallback((bar) => {
    if (!bar) return;
    setPinned((prev) => (prev && prev.date === bar.date ? null : { ...bar }));
  }, []);
  const clearPin = useCallback(() => setPinned(null), []);

  // Range switch: if the pinned date is not in the new range's history, clear.
  useEffect(() => {
    if (!pinned || !Array.isArray(snap?.history)) return;
    const exists = snap.history.some((b) => b?.date === pinned.date);
    if (!exists) setPinned(null);
  }, [snap, pinned]);

  // Escape key clears pin.
  useEffect(() => {
    if (!pinned) return;
    const onKey = (e) => {
      if (e.key === "Escape") setPinned(null);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [pinned]);

  const q = snap?.quote;
  const up = (q?.change ?? 0) >= 0;
  const changeColor = up ? "#22C55E" : "#EF4444";
  const TrendIcon = up ? TrendingUp : TrendingDown;

  const barsForChart = useMemo(() => snap?.history || [], [snap]);
  const historyEmpty = !loading && Array.isArray(snap?.history) && snap.history.length === 0;

  // Effective readout: pin > hover > latest fallback (already emitted by chart).
  const activeReadout = pinned ? { ...pinned, isLast: false } : readout;
  const readoutUp = activeReadout && typeof activeReadout.open === "number"
    ? activeReadout.close >= activeReadout.open : true;

  // Delta vs current live price when pinned.
  const pinDelta = useMemo(() => {
    if (!pinned || !q || typeof q.price !== "number" || typeof pinned.close !== "number" || pinned.close === 0) {
      return null;
    }
    const abs = q.price - pinned.close;
    const pct = (abs / pinned.close) * 100;
    return { abs, pct };
  }, [pinned, q]);

  if (loading) {
    return (
      <div
        className="w-full flex items-center justify-center text-[#8A8075] text-sm font-mono uppercase tracking-[0.2em]"
        style={{ minHeight: 380 }}
        data-testid="quote-loading"
      >
        <RefreshCw className="h-4 w-4 mr-3 animate-spin" />
        Loading quote…
      </div>
    );
  }

  if (loadErr || !snap || !snap.quote || snap.has_ever_fetched === false) {
    if (typeof renderFallback === "function") return renderFallback({ reason: loadErr || "no_data_yet" });
    return null;
  }

  return (
    <div
      className="w-full grid grid-cols-1 lg:grid-cols-12 gap-0 bg-[#14110E]"
      data-testid="quote-panel"
    >
      {/* Left column — headline metrics + 52W bar */}
      <div className="lg:col-span-5 p-6 sm:p-8 lg:p-10 border-b lg:border-b-0 lg:border-r border-[#3A2E22]">
        <div className="mt-4 flex items-center gap-3 text-[11px] font-mono uppercase tracking-[0.22em] text-[#F26A1B]">
          <span className="h-1.5 w-1.5 rounded-full bg-[#F26A1B] animate-pulse" />
          {snap.stale ? "Delayed quote" : "Live quote"} · Yahoo Finance
          {snap.feed_symbol && <span className="text-[#8A8075]">· feed {snap.feed_symbol}</span>}
        </div>
        <div className="mt-4 flex items-baseline gap-3">
          <span className="font-display text-4xl sm:text-5xl lg:text-6xl text-white font-semibold tracking-tight" data-testid="quote-symbol">
            {COMPANY.ticker}
          </span>
          <span className="font-mono text-xs sm:text-sm text-[#A79E93] uppercase tracking-[0.22em]">
            {COMPANY.market}
          </span>
        </div>
        <div className="mt-6 flex items-baseline gap-4 flex-wrap">
          <span className="font-display text-4xl sm:text-5xl text-white font-semibold" data-testid="quote-price">
            ${fmtPrice(q.price)}
          </span>
          <span
            className="inline-flex items-center gap-1.5 font-mono text-sm sm:text-base font-semibold"
            style={{ color: changeColor }}
            data-testid="quote-change"
          >
            <TrendIcon className="h-4 w-4" />
            {fmtChange(q.change)}
            <span className="text-[#A79E93] font-normal">({fmtPct(q.change_pct)})</span>
          </span>
        </div>
        <div className="mt-2 font-mono text-[11px] uppercase tracking-[0.2em] text-[#8A8075]" data-testid="quote-timestamp">
          As of {fmtQuoteTime(q.quote_date, q.quote_time)}
          {snap.stale && <span className="text-[#F26A1B]"> · stale (cached)</span>}
        </div>

        {snap.stats_52w && (
          <Range52WBar
            low={snap.stats_52w.low}
            high={snap.stats_52w.high}
            price={q.price}
            lowDate={snap.stats_52w.low_date}
            highDate={snap.stats_52w.high_date}
          />
        )}

        <dl className="mt-8 grid grid-cols-2 gap-x-6 gap-y-4 font-mono text-xs">
          <MetaRow label="Open" value={fmtPrice(q.open)} testId="quote-open" />
          <MetaRow label="Prev Close" value={fmtPrice(q.previous_close)} testId="quote-prev" />
          <MetaRow label="Day High" value={fmtPrice(q.high)} testId="quote-high" />
          <MetaRow label="Day Low" value={fmtPrice(q.low)} testId="quote-low" />
          <MetaRow label="Volume" value={fmtVolume(q.volume)} testId="quote-volume" />
          <MetaRow label="Source" value="Yahoo Finance" testId="quote-source" />
        </dl>
      </div>

      {/* Right column — chart + volume + readout + range pills + external links */}
      <div className="lg:col-span-7 p-6 sm:p-8 lg:p-10 bg-[#1A1512] flex flex-col">
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <div className="flex items-center gap-3 flex-wrap">
            <div className="eyebrow text-[#A79E93]" data-testid="chart-range-label">
              Range · <span className="text-white">{range}</span>
            </div>
            <div className="font-mono text-[11px] uppercase tracking-[0.2em] text-[#8A8075]">
              {range === "5Y" ? "Weekly OHLC" : "Daily OHLC"}
            </div>
          </div>
          <div className="flex items-center gap-2 flex-wrap" data-testid="chart-controls">
            {/* Chart-type toggle */}
            <div className="flex items-center gap-0.5 border border-[#4A3D30] p-0.5" data-testid="chart-type-toggle">
              <button
                type="button"
                onClick={() => setChartType("candles")}
                aria-pressed={chartType === "candles"}
                className={
                  "inline-flex items-center gap-1.5 px-2.5 py-1 text-[11px] font-mono uppercase tracking-[0.14em] transition-colors " +
                  (chartType === "candles"
                    ? "bg-[#F26A1B] text-white"
                    : "text-[#F5F2EB] hover:text-white")
                }
                data-testid="chart-type-candles"
              >
                <CandlestickChart className="h-3 w-3" /> Candles
              </button>
              <button
                type="button"
                onClick={() => setChartType("line")}
                aria-pressed={chartType === "line"}
                className={
                  "inline-flex items-center gap-1.5 px-2.5 py-1 text-[11px] font-mono uppercase tracking-[0.14em] transition-colors " +
                  (chartType === "line"
                    ? "bg-[#F26A1B] text-white"
                    : "text-[#F5F2EB] hover:text-white")
                }
                data-testid="chart-type-line"
              >
                <LineChart className="h-3 w-3" /> Line
              </button>
            </div>
            <div className="flex items-center gap-1.5" data-testid="chart-range-pills">
              {RANGE_PILLS.map((r) => (
                <button
                  type="button"
                  key={r}
                  onClick={() => onSelectRange(r)}
                  aria-pressed={range === r}
                  className={
                    "px-2.5 py-1 text-[11px] font-mono uppercase tracking-[0.14em] border transition-colors " +
                    (range === r
                      ? "border-[#F26A1B] bg-[#F26A1B] text-white"
                      : "border-[#4A3D30] text-[#F5F2EB] hover:border-white hover:text-white")
                  }
                  data-testid={`chart-range-${r.toLowerCase()}`}
                >
                  {r}
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* Bloomberg-style OHLCV readout strip */}
        <div
          className="mt-4 grid grid-cols-6 sm:grid-cols-12 gap-x-3 gap-y-1 items-center py-2 px-3 border bg-[#14110E]/60 font-mono text-[11px] uppercase tracking-[0.14em] transition-colors"
          style={{ borderColor: pinned ? "#F26A1B" : "#3A2E22" }}
          data-testid="chart-readout"
        >
          <div className="col-span-6 sm:col-span-2 flex items-center gap-2">
            <span className="text-[#8A8075]">DATE</span>
            <span className="text-white truncate" data-testid="chart-readout-date">
              {activeReadout?.date ?? "—"}
            </span>
          </div>
          <ReadoutCell label="O" value={fmtPrice(activeReadout?.open)}   testId="chart-readout-open"   />
          <ReadoutCell label="H" value={fmtPrice(activeReadout?.high)}   testId="chart-readout-high"   />
          <ReadoutCell label="L" value={fmtPrice(activeReadout?.low)}    testId="chart-readout-low"    />
          <ReadoutCell
            label="C"
            value={fmtPrice(activeReadout?.close)}
            testId="chart-readout-close"
            valueClass={readoutUp ? "text-[#22C55E]" : "text-[#EF4444]"}
          />
          <ReadoutCell
            label="VOL"
            value={fmtVolume(activeReadout?.volume)}
            testId="chart-readout-volume"
          />
          <div className="col-span-6 sm:col-span-1 flex items-center justify-end gap-2" data-testid="chart-readout-status">
            {pinned ? (
              <>
                <span className="text-[#F26A1B] font-semibold">PINNED</span>
                <button
                  type="button"
                  onClick={clearPin}
                  aria-label="Clear pinned bar"
                  className="inline-flex items-center justify-center h-5 w-5 border border-[#F26A1B] text-[#F26A1B] hover:bg-[#F26A1B] hover:text-white transition-colors"
                  data-testid="chart-pin-clear"
                >
                  ×
                </button>
              </>
            ) : (
              <span className="text-[#8A8075]">
                {activeReadout?.isLast ? "LATEST" : activeReadout ? "HOVER" : ""}
              </span>
            )}
            {rangeLoading && (
              <span className="inline-flex items-center gap-1 text-[#8A8075]">
                <RefreshCw className="h-3 w-3 animate-spin" />
              </span>
            )}
          </div>
          {pinned && pinDelta && (
            <div
              className="col-span-6 sm:col-span-12 pt-1 border-t border-[#3A2E22]/60 text-[10px] text-[#F5F2EB] tracking-[0.14em]"
              data-testid="chart-pin-delta"
            >
              <span className="text-[#8A8075]">vs current</span>{" "}
              <span
                className="font-semibold"
                style={{ color: pinDelta.abs >= 0 ? "#22C55E" : "#EF4444" }}
              >
                {pinDelta.abs >= 0 ? "+" : ""}
                {pinDelta.abs.toFixed(4)} ({pinDelta.pct >= 0 ? "+" : ""}
                {pinDelta.pct.toFixed(2)}%)
              </span>
              <span className="text-[#8A8075]"> · pin persists on hover · press Esc to clear</span>
            </div>
          )}
        </div>

        {/* Chart or empty state */}
        <div className="mt-3 flex-1 min-h-[280px] relative" data-testid="chart-container">
          {historyEmpty ? (
            <div className="h-[320px] flex items-center justify-center border border-[#3A2E22] text-[#8A8075] text-sm font-mono uppercase tracking-[0.18em]" data-testid="chart-empty">
              No history for this range
            </div>
          ) : (
            <InteractiveChart
              key={`${range}-${chartType}`}
              bars={barsForChart}
              chartType={chartType}
              height={320}
              pinnedTime={pinned?.date || null}
              onCrosshairChange={setReadout}
              onBarClick={onBarClick}
            />
          )}
          {rangeLoading && (
            <div
              className="absolute inset-0 flex items-center justify-center bg-[#1A1512]/40 text-[#F5F2EB] text-xs font-mono uppercase tracking-[0.18em]"
              style={{ pointerEvents: "none" }}
              data-testid="chart-loading-overlay"
            >
              <RefreshCw className="h-3.5 w-3.5 mr-2 animate-spin" /> updating
            </div>
          )}
        </div>

        <div className="mt-6 grid grid-cols-1 sm:grid-cols-3 gap-2">
          <a
            href={TRADINGVIEW_SYMBOL_URL}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center justify-between gap-2 border border-[#4A3D30] hover:border-white text-white px-4 py-3 text-xs font-semibold transition-colors"
            data-testid="external-tv-link"
          >
            <span>Advanced chart on TradingView</span>
            <ExternalLink className="h-3.5 w-3.5 flex-shrink-0" />
          </a>
          <a
            href={OTC_MARKETS_URL}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center justify-between gap-2 border border-[#4A3D30] hover:border-white text-white px-4 py-3 text-xs font-semibold transition-colors"
            data-testid="external-otc-link"
          >
            <span>Live quote · OTC Markets</span>
            <ExternalLink className="h-3.5 w-3.5 flex-shrink-0" />
          </a>
          <a
            href={SEC_EDGAR_CIK_URL}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center justify-between gap-2 border border-[#F26A1B] btn-gradient rounded-full px-4 py-3 text-xs font-semibold transition-colors"
            data-testid="external-sec-link"
          >
            <span>SEC EDGAR filings</span>
            <ArrowUpRight className="h-3.5 w-3.5 flex-shrink-0" />
          </a>
        </div>
      </div>
    </div>
  );
}

function ReadoutCell({ label, value, testId, valueClass = "text-white" }) {
  return (
    <div className="col-span-2 sm:col-span-2 flex items-center gap-2" data-testid={testId}>
      <span className="text-[#8A8075]">{label}</span>
      <span className={`truncate ${valueClass}`} data-testid={`${testId}-value`}>
        {value}
      </span>
    </div>
  );
}

function MetaRow({ label, value, testId }) {
  return (
    <div className="flex flex-col" data-testid={testId}>
      <dt className="text-[10px] uppercase tracking-[0.2em] text-[#8A8075]">{label}</dt>
      <dd className="mt-1 text-sm text-white font-semibold">{value}</dd>
    </div>
  );
}
